/**
 * Country constants and supported markets
 *
 * Centralized country data for use across all portals.
 */

// ═══════════════════════════════════════════════════════════════════════
// THE "NOT CHOSEN YET" SENTINEL
// ═══════════════════════════════════════════════════════════════════════

/**
 * `journey_instances.destination_country` when the destination is not known.
 *
 * The column is NOT NULL, so a journey created before its destination has been
 * chosen — an acquisition placeholder, a QultureUp auto-create, a guide run
 * that never got that far — must carry SOMETHING. This is that something, and
 * it is not a country: it is the absence of one, in a column that cannot say so.
 *
 * It is a named constant because two separate rules have to agree on it and
 * were drifting apart:
 *
 *   1. the READERS that match an invitation with no destination to an existing
 *      journey (`accept-journey-selection.ts`, `talent-invitations.service.ts`);
 *   2. the journey factory (`createForRoute` / `resolveForRoute`), which must
 *      NOT treat it as a route — two journeys that merely both lack a
 *      destination are not the same journey, and matching on it would let a
 *      citizen's QultureUp journey collide with a company's acquisition
 *      placeholder across tenants.
 *
 * One writer used `'XXX'` instead, so its rows were invisible to (1); see
 * `scripts/recovery/w2-prb-retire-xxx-destination.sql`.
 *
 * NOT the legacy ORIGIN sentinel. `origin_country` is nullable and migration
 * 0046 rewrote its `'UNK'` values to NULL, which is the honest representation
 * there. Destination cannot be NULL, so it keeps this.
 */
export const UNKNOWN_DESTINATION = 'UNK';

/** Is this a real destination, as opposed to "not chosen yet"? */
export function isKnownDestination(code: string | null | undefined): code is string {
  return !!code && code !== UNKNOWN_DESTINATION;
}

// ═══════════════════════════════════════════════════════════════════════
// COUNTRY DATA
// ═══════════════════════════════════════════════════════════════════════

export interface Country {
  code: string;
  name: string;
  emoji: string;
  phoneCode: string;
}

/**
 * Complete list of countries (ISO 3166-1 alpha-2)
 */
export const COUNTRIES: Country[] = [
  { code: 'AF', name: 'Afghanistan', emoji: '🇦🇫', phoneCode: '+93' },
  { code: 'AL', name: 'Albania', emoji: '🇦🇱', phoneCode: '+355' },
  { code: 'DZ', name: 'Algeria', emoji: '🇩🇿', phoneCode: '+213' },
  { code: 'AD', name: 'Andorra', emoji: '🇦🇩', phoneCode: '+376' },
  { code: 'AO', name: 'Angola', emoji: '🇦🇴', phoneCode: '+244' },
  { code: 'AG', name: 'Antigua and Barbuda', emoji: '🇦🇬', phoneCode: '+1-268' },
  { code: 'AR', name: 'Argentina', emoji: '🇦🇷', phoneCode: '+54' },
  { code: 'AM', name: 'Armenia', emoji: '🇦🇲', phoneCode: '+374' },
  { code: 'AU', name: 'Australia', emoji: '🇦🇺', phoneCode: '+61' },
  { code: 'AT', name: 'Austria', emoji: '🇦🇹', phoneCode: '+43' },
  { code: 'AZ', name: 'Azerbaijan', emoji: '🇦🇿', phoneCode: '+994' },
  { code: 'BS', name: 'Bahamas', emoji: '🇧🇸', phoneCode: '+1-242' },
  { code: 'BH', name: 'Bahrain', emoji: '🇧🇭', phoneCode: '+973' },
  { code: 'BD', name: 'Bangladesh', emoji: '🇧🇩', phoneCode: '+880' },
  { code: 'BB', name: 'Barbados', emoji: '🇧🇧', phoneCode: '+1-246' },
  { code: 'BY', name: 'Belarus', emoji: '🇧🇾', phoneCode: '+375' },
  { code: 'BE', name: 'Belgium', emoji: '🇧🇪', phoneCode: '+32' },
  { code: 'BZ', name: 'Belize', emoji: '🇧🇿', phoneCode: '+501' },
  { code: 'BJ', name: 'Benin', emoji: '🇧🇯', phoneCode: '+229' },
  { code: 'BT', name: 'Bhutan', emoji: '🇧🇹', phoneCode: '+975' },
  { code: 'BO', name: 'Bolivia', emoji: '🇧🇴', phoneCode: '+591' },
  { code: 'BA', name: 'Bosnia and Herzegovina', emoji: '🇧🇦', phoneCode: '+387' },
  { code: 'BW', name: 'Botswana', emoji: '🇧🇼', phoneCode: '+267' },
  { code: 'BR', name: 'Brazil', emoji: '🇧🇷', phoneCode: '+55' },
  { code: 'BN', name: 'Brunei', emoji: '🇧🇳', phoneCode: '+673' },
  { code: 'BG', name: 'Bulgaria', emoji: '🇧🇬', phoneCode: '+359' },
  { code: 'BF', name: 'Burkina Faso', emoji: '🇧🇫', phoneCode: '+226' },
  { code: 'BI', name: 'Burundi', emoji: '🇧🇮', phoneCode: '+257' },
  { code: 'CV', name: 'Cabo Verde', emoji: '🇨🇻', phoneCode: '+238' },
  { code: 'KH', name: 'Cambodia', emoji: '🇰🇭', phoneCode: '+855' },
  { code: 'CM', name: 'Cameroon', emoji: '🇨🇲', phoneCode: '+237' },
  { code: 'CA', name: 'Canada', emoji: '🇨🇦', phoneCode: '+1' },
  { code: 'CF', name: 'Central African Republic', emoji: '🇨🇫', phoneCode: '+236' },
  { code: 'TD', name: 'Chad', emoji: '🇹🇩', phoneCode: '+235' },
  { code: 'CL', name: 'Chile', emoji: '🇨🇱', phoneCode: '+56' },
  { code: 'CN', name: 'China', emoji: '🇨🇳', phoneCode: '+86' },
  { code: 'CO', name: 'Colombia', emoji: '🇨🇴', phoneCode: '+57' },
  { code: 'KM', name: 'Comoros', emoji: '🇰🇲', phoneCode: '+269' },
  { code: 'CG', name: 'Congo', emoji: '🇨🇬', phoneCode: '+242' },
  { code: 'CD', name: 'Congo (DRC)', emoji: '🇨🇩', phoneCode: '+243' },
  { code: 'CR', name: 'Costa Rica', emoji: '🇨🇷', phoneCode: '+506' },
  { code: 'CI', name: "Côte d'Ivoire", emoji: '🇨🇮', phoneCode: '+225' },
  { code: 'HR', name: 'Croatia', emoji: '🇭🇷', phoneCode: '+385' },
  { code: 'CU', name: 'Cuba', emoji: '🇨🇺', phoneCode: '+53' },
  { code: 'CY', name: 'Cyprus', emoji: '🇨🇾', phoneCode: '+357' },
  { code: 'CZ', name: 'Czech Republic', emoji: '🇨🇿', phoneCode: '+420' },
  { code: 'DK', name: 'Denmark', emoji: '🇩🇰', phoneCode: '+45' },
  { code: 'DJ', name: 'Djibouti', emoji: '🇩🇯', phoneCode: '+253' },
  { code: 'DM', name: 'Dominica', emoji: '🇩🇲', phoneCode: '+1-767' },
  { code: 'DO', name: 'Dominican Republic', emoji: '🇩🇴', phoneCode: '+1-809' },
  { code: 'EC', name: 'Ecuador', emoji: '🇪🇨', phoneCode: '+593' },
  { code: 'EG', name: 'Egypt', emoji: '🇪🇬', phoneCode: '+20' },
  { code: 'SV', name: 'El Salvador', emoji: '🇸🇻', phoneCode: '+503' },
  { code: 'GQ', name: 'Equatorial Guinea', emoji: '🇬🇶', phoneCode: '+240' },
  { code: 'ER', name: 'Eritrea', emoji: '🇪🇷', phoneCode: '+291' },
  { code: 'EE', name: 'Estonia', emoji: '🇪🇪', phoneCode: '+372' },
  { code: 'SZ', name: 'Eswatini', emoji: '🇸🇿', phoneCode: '+268' },
  { code: 'ET', name: 'Ethiopia', emoji: '🇪🇹', phoneCode: '+251' },
  { code: 'FJ', name: 'Fiji', emoji: '🇫🇯', phoneCode: '+679' },
  { code: 'FI', name: 'Finland', emoji: '🇫🇮', phoneCode: '+358' },
  { code: 'FR', name: 'France', emoji: '🇫🇷', phoneCode: '+33' },
  { code: 'GA', name: 'Gabon', emoji: '🇬🇦', phoneCode: '+241' },
  { code: 'GM', name: 'Gambia', emoji: '🇬🇲', phoneCode: '+220' },
  { code: 'GE', name: 'Georgia', emoji: '🇬🇪', phoneCode: '+995' },
  { code: 'DE', name: 'Germany', emoji: '🇩🇪', phoneCode: '+49' },
  { code: 'GH', name: 'Ghana', emoji: '🇬🇭', phoneCode: '+233' },
  { code: 'GR', name: 'Greece', emoji: '🇬🇷', phoneCode: '+30' },
  { code: 'GD', name: 'Grenada', emoji: '🇬🇩', phoneCode: '+1-473' },
  { code: 'GT', name: 'Guatemala', emoji: '🇬🇹', phoneCode: '+502' },
  { code: 'GN', name: 'Guinea', emoji: '🇬🇳', phoneCode: '+224' },
  { code: 'GW', name: 'Guinea-Bissau', emoji: '🇬🇼', phoneCode: '+245' },
  { code: 'GY', name: 'Guyana', emoji: '🇬🇾', phoneCode: '+592' },
  { code: 'HT', name: 'Haiti', emoji: '🇭🇹', phoneCode: '+509' },
  { code: 'HN', name: 'Honduras', emoji: '🇭🇳', phoneCode: '+504' },
  { code: 'HK', name: 'Hong Kong', emoji: '🇭🇰', phoneCode: '+852' },
  { code: 'HU', name: 'Hungary', emoji: '🇭🇺', phoneCode: '+36' },
  { code: 'IS', name: 'Iceland', emoji: '🇮🇸', phoneCode: '+354' },
  { code: 'IN', name: 'India', emoji: '🇮🇳', phoneCode: '+91' },
  { code: 'ID', name: 'Indonesia', emoji: '🇮🇩', phoneCode: '+62' },
  { code: 'IR', name: 'Iran', emoji: '🇮🇷', phoneCode: '+98' },
  { code: 'IQ', name: 'Iraq', emoji: '🇮🇶', phoneCode: '+964' },
  { code: 'IE', name: 'Ireland', emoji: '🇮🇪', phoneCode: '+353' },
  { code: 'IL', name: 'Israel', emoji: '🇮🇱', phoneCode: '+972' },
  { code: 'IT', name: 'Italy', emoji: '🇮🇹', phoneCode: '+39' },
  { code: 'JM', name: 'Jamaica', emoji: '🇯🇲', phoneCode: '+1-876' },
  { code: 'JP', name: 'Japan', emoji: '🇯🇵', phoneCode: '+81' },
  { code: 'JO', name: 'Jordan', emoji: '🇯🇴', phoneCode: '+962' },
  { code: 'KZ', name: 'Kazakhstan', emoji: '🇰🇿', phoneCode: '+7' },
  { code: 'KE', name: 'Kenya', emoji: '🇰🇪', phoneCode: '+254' },
  { code: 'KI', name: 'Kiribati', emoji: '🇰🇮', phoneCode: '+686' },
  { code: 'KW', name: 'Kuwait', emoji: '🇰🇼', phoneCode: '+965' },
  { code: 'KG', name: 'Kyrgyzstan', emoji: '🇰🇬', phoneCode: '+996' },
  { code: 'LA', name: 'Laos', emoji: '🇱🇦', phoneCode: '+856' },
  { code: 'LV', name: 'Latvia', emoji: '🇱🇻', phoneCode: '+371' },
  { code: 'LB', name: 'Lebanon', emoji: '🇱🇧', phoneCode: '+961' },
  { code: 'LS', name: 'Lesotho', emoji: '🇱🇸', phoneCode: '+266' },
  { code: 'LR', name: 'Liberia', emoji: '🇱🇷', phoneCode: '+231' },
  { code: 'LY', name: 'Libya', emoji: '🇱🇾', phoneCode: '+218' },
  { code: 'LI', name: 'Liechtenstein', emoji: '🇱🇮', phoneCode: '+423' },
  { code: 'LT', name: 'Lithuania', emoji: '🇱🇹', phoneCode: '+370' },
  { code: 'LU', name: 'Luxembourg', emoji: '🇱🇺', phoneCode: '+352' },
  { code: 'MO', name: 'Macao', emoji: '🇲🇴', phoneCode: '+853' },
  { code: 'MG', name: 'Madagascar', emoji: '🇲🇬', phoneCode: '+261' },
  { code: 'MW', name: 'Malawi', emoji: '🇲🇼', phoneCode: '+265' },
  { code: 'MY', name: 'Malaysia', emoji: '🇲🇾', phoneCode: '+60' },
  { code: 'MV', name: 'Maldives', emoji: '🇲🇻', phoneCode: '+960' },
  { code: 'ML', name: 'Mali', emoji: '🇲🇱', phoneCode: '+223' },
  { code: 'MT', name: 'Malta', emoji: '🇲🇹', phoneCode: '+356' },
  { code: 'MH', name: 'Marshall Islands', emoji: '🇲🇭', phoneCode: '+692' },
  { code: 'MR', name: 'Mauritania', emoji: '🇲🇷', phoneCode: '+222' },
  { code: 'MU', name: 'Mauritius', emoji: '🇲🇺', phoneCode: '+230' },
  { code: 'MX', name: 'Mexico', emoji: '🇲🇽', phoneCode: '+52' },
  { code: 'FM', name: 'Micronesia', emoji: '🇫🇲', phoneCode: '+691' },
  { code: 'MD', name: 'Moldova', emoji: '🇲🇩', phoneCode: '+373' },
  { code: 'MC', name: 'Monaco', emoji: '🇲🇨', phoneCode: '+377' },
  { code: 'MN', name: 'Mongolia', emoji: '🇲🇳', phoneCode: '+976' },
  { code: 'ME', name: 'Montenegro', emoji: '🇲🇪', phoneCode: '+382' },
  { code: 'MA', name: 'Morocco', emoji: '🇲🇦', phoneCode: '+212' },
  { code: 'MZ', name: 'Mozambique', emoji: '🇲🇿', phoneCode: '+258' },
  { code: 'MM', name: 'Myanmar', emoji: '🇲🇲', phoneCode: '+95' },
  { code: 'NA', name: 'Namibia', emoji: '🇳🇦', phoneCode: '+264' },
  { code: 'NR', name: 'Nauru', emoji: '🇳🇷', phoneCode: '+674' },
  { code: 'NP', name: 'Nepal', emoji: '🇳🇵', phoneCode: '+977' },
  { code: 'NL', name: 'Netherlands', emoji: '🇳🇱', phoneCode: '+31' },
  { code: 'NZ', name: 'New Zealand', emoji: '🇳🇿', phoneCode: '+64' },
  { code: 'NI', name: 'Nicaragua', emoji: '🇳🇮', phoneCode: '+505' },
  { code: 'NE', name: 'Niger', emoji: '🇳🇪', phoneCode: '+227' },
  { code: 'NG', name: 'Nigeria', emoji: '🇳🇬', phoneCode: '+234' },
  { code: 'KP', name: 'North Korea', emoji: '🇰🇵', phoneCode: '+850' },
  { code: 'MK', name: 'North Macedonia', emoji: '🇲🇰', phoneCode: '+389' },
  { code: 'NO', name: 'Norway', emoji: '🇳🇴', phoneCode: '+47' },
  { code: 'OM', name: 'Oman', emoji: '🇴🇲', phoneCode: '+968' },
  { code: 'PK', name: 'Pakistan', emoji: '🇵🇰', phoneCode: '+92' },
  { code: 'PW', name: 'Palau', emoji: '🇵🇼', phoneCode: '+680' },
  { code: 'PS', name: 'Palestine', emoji: '🇵🇸', phoneCode: '+970' },
  { code: 'PA', name: 'Panama', emoji: '🇵🇦', phoneCode: '+507' },
  { code: 'PG', name: 'Papua New Guinea', emoji: '🇵🇬', phoneCode: '+675' },
  { code: 'PY', name: 'Paraguay', emoji: '🇵🇾', phoneCode: '+595' },
  { code: 'PE', name: 'Peru', emoji: '🇵🇪', phoneCode: '+51' },
  { code: 'PH', name: 'Philippines', emoji: '🇵🇭', phoneCode: '+63' },
  { code: 'PL', name: 'Poland', emoji: '🇵🇱', phoneCode: '+48' },
  { code: 'PT', name: 'Portugal', emoji: '🇵🇹', phoneCode: '+351' },
  { code: 'QA', name: 'Qatar', emoji: '🇶🇦', phoneCode: '+974' },
  { code: 'RO', name: 'Romania', emoji: '🇷🇴', phoneCode: '+40' },
  { code: 'RU', name: 'Russia', emoji: '🇷🇺', phoneCode: '+7' },
  { code: 'RW', name: 'Rwanda', emoji: '🇷🇼', phoneCode: '+250' },
  { code: 'KN', name: 'Saint Kitts and Nevis', emoji: '🇰🇳', phoneCode: '+1-869' },
  { code: 'LC', name: 'Saint Lucia', emoji: '🇱🇨', phoneCode: '+1-758' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines', emoji: '🇻🇨', phoneCode: '+1-784' },
  { code: 'WS', name: 'Samoa', emoji: '🇼🇸', phoneCode: '+685' },
  { code: 'SM', name: 'San Marino', emoji: '🇸🇲', phoneCode: '+378' },
  { code: 'ST', name: 'São Tomé and Príncipe', emoji: '🇸🇹', phoneCode: '+239' },
  { code: 'SA', name: 'Saudi Arabia', emoji: '🇸🇦', phoneCode: '+966' },
  { code: 'SN', name: 'Senegal', emoji: '🇸🇳', phoneCode: '+221' },
  { code: 'RS', name: 'Serbia', emoji: '🇷🇸', phoneCode: '+381' },
  { code: 'SC', name: 'Seychelles', emoji: '🇸🇨', phoneCode: '+248' },
  { code: 'SL', name: 'Sierra Leone', emoji: '🇸🇱', phoneCode: '+232' },
  { code: 'SG', name: 'Singapore', emoji: '🇸🇬', phoneCode: '+65' },
  { code: 'SK', name: 'Slovakia', emoji: '🇸🇰', phoneCode: '+421' },
  { code: 'SI', name: 'Slovenia', emoji: '🇸🇮', phoneCode: '+386' },
  { code: 'SB', name: 'Solomon Islands', emoji: '🇸🇧', phoneCode: '+677' },
  { code: 'SO', name: 'Somalia', emoji: '🇸🇴', phoneCode: '+252' },
  { code: 'ZA', name: 'South Africa', emoji: '🇿🇦', phoneCode: '+27' },
  { code: 'KR', name: 'South Korea', emoji: '🇰🇷', phoneCode: '+82' },
  { code: 'SS', name: 'South Sudan', emoji: '🇸🇸', phoneCode: '+211' },
  { code: 'ES', name: 'Spain', emoji: '🇪🇸', phoneCode: '+34' },
  { code: 'LK', name: 'Sri Lanka', emoji: '🇱🇰', phoneCode: '+94' },
  { code: 'SD', name: 'Sudan', emoji: '🇸🇩', phoneCode: '+249' },
  { code: 'SR', name: 'Suriname', emoji: '🇸🇷', phoneCode: '+597' },
  { code: 'SE', name: 'Sweden', emoji: '🇸🇪', phoneCode: '+46' },
  { code: 'CH', name: 'Switzerland', emoji: '🇨🇭', phoneCode: '+41' },
  { code: 'SY', name: 'Syria', emoji: '🇸🇾', phoneCode: '+963' },
  { code: 'TW', name: 'Taiwan', emoji: '🇹🇼', phoneCode: '+886' },
  { code: 'TJ', name: 'Tajikistan', emoji: '🇹🇯', phoneCode: '+992' },
  { code: 'TZ', name: 'Tanzania', emoji: '🇹🇿', phoneCode: '+255' },
  { code: 'TH', name: 'Thailand', emoji: '🇹🇭', phoneCode: '+66' },
  { code: 'TL', name: 'Timor-Leste', emoji: '🇹🇱', phoneCode: '+670' },
  { code: 'TG', name: 'Togo', emoji: '🇹🇬', phoneCode: '+228' },
  { code: 'TO', name: 'Tonga', emoji: '🇹🇴', phoneCode: '+676' },
  { code: 'TT', name: 'Trinidad and Tobago', emoji: '🇹🇹', phoneCode: '+1-868' },
  { code: 'TN', name: 'Tunisia', emoji: '🇹🇳', phoneCode: '+216' },
  { code: 'TR', name: 'Turkey', emoji: '🇹🇷', phoneCode: '+90' },
  { code: 'TM', name: 'Turkmenistan', emoji: '🇹🇲', phoneCode: '+993' },
  { code: 'TV', name: 'Tuvalu', emoji: '🇹🇻', phoneCode: '+688' },
  { code: 'UG', name: 'Uganda', emoji: '🇺🇬', phoneCode: '+256' },
  { code: 'UA', name: 'Ukraine', emoji: '🇺🇦', phoneCode: '+380' },
  { code: 'AE', name: 'United Arab Emirates', emoji: '🇦🇪', phoneCode: '+971' },
  { code: 'GB', name: 'United Kingdom', emoji: '🇬🇧', phoneCode: '+44' },
  { code: 'US', name: 'United States', emoji: '🇺🇸', phoneCode: '+1' },
  { code: 'UY', name: 'Uruguay', emoji: '🇺🇾', phoneCode: '+598' },
  { code: 'UZ', name: 'Uzbekistan', emoji: '🇺🇿', phoneCode: '+998' },
  { code: 'VU', name: 'Vanuatu', emoji: '🇻🇺', phoneCode: '+678' },
  { code: 'VA', name: 'Vatican City', emoji: '🇻🇦', phoneCode: '+379' },
  { code: 'VE', name: 'Venezuela', emoji: '🇻🇪', phoneCode: '+58' },
  { code: 'VN', name: 'Vietnam', emoji: '🇻🇳', phoneCode: '+84' },
  { code: 'YE', name: 'Yemen', emoji: '🇾🇪', phoneCode: '+967' },
  { code: 'ZM', name: 'Zambia', emoji: '🇿🇲', phoneCode: '+260' },
  { code: 'ZW', name: 'Zimbabwe', emoji: '🇿🇼', phoneCode: '+263' },
];

// ═══════════════════════════════════════════════════════════════════════
// DESTINATION COUNTRIES (Supported markets)
// ═══════════════════════════════════════════════════════════════════════

export const DESTINATION_COUNTRIES = {
  PT: 'PT', // Portugal
  ES: 'ES', // Spain
  DE: 'DE', // Germany
  IT: 'IT', // Italy
  FR: 'FR', // France
  NL: 'NL', // Netherlands
  IE: 'IE', // Ireland
  GB: 'GB', // United Kingdom
} as const;

export type DestinationCountry = (typeof DESTINATION_COUNTRIES)[keyof typeof DESTINATION_COUNTRIES];

// ═══════════════════════════════════════════════════════════════════════
// LOOKUP MAPS (Generated from COUNTRIES array)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Country code to name mapping
 */
export const COUNTRY_LABELS: Record<string, string> = Object.fromEntries(
  COUNTRIES.map((c) => [c.code, c.name])
);

/**
 * Country code to phone code mapping
 */
export const COUNTRY_PHONE_CODES: Record<string, string> = Object.fromEntries(
  COUNTRIES.map((c) => [c.code, c.phoneCode])
);

// ═══════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Get country by code
 */
export function getCountryByCode(code: string): Country | undefined {
  return COUNTRIES.find((c) => c.code === code);
}

/**
 * Get country name by code
 */
export function getCountryName(code: string): string {
  return COUNTRY_LABELS[code] || code;
}

/**
 * Flag EMOJI for a country code, or `null` when the code is not a country.
 *
 * ── REACT NATIVE ONLY. Web must use `<CountryFlag>` from `@tonnie/ui`. ───────
 *
 * A flag emoji is a pair of Unicode regional-indicator characters, and whether
 * it draws a flag is entirely up to the platform's emoji font. iOS and Android
 * ship one that has them; Windows does not, so Chrome falls back to rendering
 * the two letters. Windows is about 45% of this platform's desktop traffic, and
 * for all of it the journey selector, the language picker and every route
 * header showed `PT` or an empty box instead of a flag. The web fix is an
 * `<img>` pointing at a vendored SVG; mobile is exempt (DW6-4) because the RN
 * text renderer draws from the OS image, where the glyphs exist.
 *
 * ── `null`, not a white flag (H12) ──────────────────────────────────────────
 *
 * This function used to return `'🏳️'` for anything it did not recognise. That
 * glyph renders fine EVERYWHERE, so an unknown country was indistinguishable
 * from a country whose flag is white — on macOS and iOS as much as on Windows.
 * It was never a font bug; it was an answer where there was no answer. Several
 * call sites deliberately pass `''` or `'?'` for "not chosen yet", and every one
 * of them was drawing a flag for it.
 *
 * `null` forces the caller to decide. Mobile renders nothing (an RN `<Text>`
 * child of `null` draws nothing); web renders the dashed placeholder box.
 *
 * The name changed along with the return type on purpose: `getCountryFlag` was
 * called from 30-odd web components, and a silent type widening would have left
 * every one of them compiling while rendering `null` into a template string.
 */
export function getCountryFlagEmoji(code: string | null | undefined): string | null {
  if (!code) return null;
  return COUNTRIES.find((c) => c.code === code.trim().toUpperCase())?.emoji ?? null;
}

/**
 * Get country phone code by code
 */
export function getCountryPhoneCode(code: string): string {
  return COUNTRY_PHONE_CODES[code] || '';
}

/**
 * Search countries by name or code
 */
export function searchCountries(query: string): Country[] {
  const normalizedQuery = query.toLowerCase().trim();
  if (!normalizedQuery) return COUNTRIES;

  return COUNTRIES.filter(
    (c) =>
      c.name.toLowerCase().includes(normalizedQuery) ||
      c.code.toLowerCase().includes(normalizedQuery)
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SUPPORTED ROUTES
// ═══════════════════════════════════════════════════════════════════════

/**
 * Release 1 supported routes (origin → destination)
 */
export const SUPPORTED_ROUTES = [
  { origin: 'BR', destination: 'PT' },
  { origin: 'US', destination: 'PT' },
  { origin: 'IN', destination: 'PT' },
  { origin: 'BR', destination: 'ES' },
  { origin: 'BR', destination: 'DE' },
  // Add more routes as supported
] as const;

/**
 * Check if a route is supported
 */
export function isRouteSupported(origin: string, destination: string): boolean {
  return SUPPORTED_ROUTES.some(
    (route) => route.origin === origin && route.destination === destination
  );
}
